import { omit } from 'lodash';
import React from 'react';

import { FilterDropdown } from '@/components/filters/FilterDropdown';
import type { FilterOption, GroupOption } from '@/components/filters/types';
import { ClearFiltersButton } from '@/components/ui/ClearFiltersButton';
import { SearchBar } from '@/components/ui/SearchBar';
import { defaultFilters } from '@/sections/customerManagement/config/columnDefinitions';
import { Filters } from '@/sections/customerManagement/types';
import { useAuth } from '@/state/auth';
import { CUSTOMER_TYPE_LABELS } from '@/types';

interface CustomerManagementSearchFiltersProps {
  filters: Filters;
  setFilters: (filters: Filters) => void;
}

const CustomerManagementSearchFiltersComponent: React.FC<
  CustomerManagementSearchFiltersProps
> = ({ filters, setFilters }) => {
  const { isPraetorianUser } = useAuth();
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-3">
      {/* Search Input */}
      <SearchBar
        size="sm"
        className="h-fit w-64 shrink-0 pr-2"
        placeholder="Search customers..."
        value={filters.search}
        onChange={value => setFilters({ ...filters, search: value })}
      />

      {/* Filters */}
      <FilterDropdown
        id="status"
        isLoading={false}
        label="Status"
        options={
          ['Active', 'Upcoming', 'Completed', 'Paused', 'Setup'].map(
            status => ({ value: status, label: status, id: status })
          ) as (FilterOption | GroupOption)[]
        }
        selectedValues={filters.advancedFilters?.status || []}
        onApply={selected => {
          setFilters({
            ...filters,
            advancedFilters: {
              ...filters.advancedFilters,
              status: selected.map(opt => opt.value as string),
            },
          });
        }}
        onClear={() =>
          setFilters({
            ...filters,
            advancedFilters: omit(filters.advancedFilters, 'status'),
          })
        }
        width={280}
      />

      {isPraetorianUser && (
        <FilterDropdown
          id="type"
          isLoading={false}
          label="Type"
          options={
            Object.entries(CUSTOMER_TYPE_LABELS).map(([value, label]) => ({
              value,
              label,
              id: value,
            })) as (FilterOption | GroupOption)[]
          }
          selectedValues={filters.advancedFilters?.type || []}
          onApply={selected => {
            setFilters({
              ...filters,
              advancedFilters: {
                ...filters.advancedFilters,
                type: selected.map(opt => opt.value as string),
              },
            });
          }}
          onClear={() =>
            setFilters({
              ...filters,
              advancedFilters: omit(filters.advancedFilters, 'type'),
            })
          }
          width={300}
        />
      )}
      {/* Clear Filters Button */}
      {Object.keys(filters.advancedFilters || {}).length > 0 && (
        <ClearFiltersButton
          onClick={() => setFilters(defaultFilters)}
          label="Clear filters"
        />
      )}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders when filters haven't changed
export const CustomerManagementSearchFilters = React.memo(
  CustomerManagementSearchFiltersComponent
);
